import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { Loader2 } from "lucide-react";
import Select from "react-select";

import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { API } from "../../constant/api";
import useAuthAxios from "../../utils/baseAxios";
import { errorMessage } from "../../utils/errorMessage";

/**
 * `nested` softens the scrim when opened on top of another dialog.
 * `onAdded` fires after a successful add with the new account's identifiers,
 * so a caller (e.g. the payment-page bank picker) can auto-select it once the
 * refreshed business data comes back.
 */
const AddBanks = ({
  setOpenAddBank,
  businessID,
  nested = false,
  onAdded,
}: any) => {
  const [bankCode, setBankCode] = useState<string>("");
  const [bankNumber, setBankNumber] = useState<string>("");
  const [bankName, setBankName] = useState<string>("");
  const [isChecked, setIsChecked] = useState(false);
  const queryClient = useQueryClient();
  const axiosInstance = useAuthAxios();

  const { data: allBanks, error: error1 } = useQuery({
    queryKey: ["all-banks"],
    queryFn: async () => {
      const response = await axiosInstance.get(API.getBanks);
      return response.data;
    },
    staleTime: 30 * 60_000,
    retry: 1,
  });

  useEffect(() => {
    if (error1) {
      const newError = error1 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error1]);

  const readyToVerify = bankNumber.length === 10 && !!bankCode;

  const {
    data: userName,
    error: error2,
    isFetching: verifying,
  } = useQuery({
    // The account number and bank code belong in the key — without them React
    // Query serves the first lookup's cached name after you change either one.
    queryKey: ["get-user-name", bankNumber, bankCode],
    queryFn: async () => {
      const response = await axiosInstance.get(
        API.verifyAccountNumber(bankNumber, bankCode)
      );
      return response.data;
    },
    retry: 1,
    enabled: readyToVerify,
  });

  useEffect(() => {
    if (error2) {
      const newError = error2 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error2]);

  const resolvedName: string = userName?.data?.accountName ?? "";

  const completeAddBank = useMutation({
    mutationFn: async (payload: {
      accountNumber: string;
      bankCode: string;
      makeDefault: boolean;
    }) => {
      const response = await axiosInstance.put(
        API.saveBusinessAccountDetails(businessID),
        payload
      );
      return response.data;
    },
    onSuccess: (r, variables) => {
      toast.success(r.message);
      queryClient.invalidateQueries({ queryKey: ["get-business"] });
      onAdded?.({
        accountNumber: variables.accountNumber,
        bankCode: variables.bankCode,
      });
      close();
    },
    onError: (e: any) =>
      toast.error(errorMessage(e?.response?.data?.message || e?.message)),
  });

  const close = () => {
    setOpenAddBank(false);
    setBankNumber("");
    setBankCode("");
    setBankName("");
    setIsChecked(false);
  };

  const canSubmit = !!bankName && !!resolvedName && bankNumber.length === 10;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit || completeAddBank.isPending) return;
    completeAddBank.mutate({
      accountNumber: bankNumber,
      bankCode,
      // Backend expects `makeDefault` as the request key.
      makeDefault: isChecked,
    });
  };

  const customSelectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: "transparent",
      borderColor: state.isFocused ? "#0B75C2" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 1.5px #0B75C2" : "none",
      minHeight: "50px",
      borderRadius: "0.75rem",
      fontSize: "15px",
      color: "#374151",
      outline: "none",
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#E8F3FC"
        : state.isFocused
        ? "#F3F4F6"
        : "#fff",
      color: "#1F2937",
      fontSize: "15px",
      padding: "10px 16px",
      cursor: "pointer",
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: "#374151",
      fontWeight: 500,
    }),
    menu: (provided: any) => ({
      ...provided,
      borderRadius: "0.75rem",
      boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
      zIndex: 20,
      backgroundColor: "#fff",
    }),
    // Portaled to <body>, so it must sit above the dialog overlay (z-50).
    // A Radix modal Dialog sets `pointer-events: none` on <body> while open;
    // the portaled menu inherits it and option clicks die — restore them here.
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 60,
      pointerEvents: "auto",
    }),
    input: (provided: any) => ({
      ...provided,
      color: "#374151",
      fontSize: "15px",
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#6B7280",
      fontSize: "15px",
    }),
  };

  return (
    <Dialog open onOpenChange={(next) => !next && close()}>
      <DialogContent
        overlayClassName={nested ? "bg-black/40" : undefined}
        className="font-manrope mds:max-w-[460px]"
      >
        <DialogHeader>
          <DialogTitle className="font-sora">Add bank</DialogTitle>
          <DialogDescription>
            Payouts are settled into the account you add here.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="account-number">Account number</Label>
            <Input
              id="account-number"
              inputMode="numeric"
              maxLength={10}
              value={bankNumber}
              onChange={(e) =>
                setBankNumber(e.target.value.replace(/\D/g, "").slice(0, 10))
              }
              placeholder="Enter your 10-digit account number"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Bank name</Label>
            <Select
              options={
                allBanks?.data?.map((bank: any) => ({
                  value: bank.bankCode,
                  label: bank.bankName,
                })) || []
              }
              onChange={(selected: any) => {
                setBankCode(selected.value);
                setBankName(selected.label);
              }}
              placeholder="Select bank"
              isSearchable
              classNamePrefix="react-select"
              styles={customSelectStyles}
              // Render the menu in a body portal so the dialog's overflow can't clip it.
              menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
              menuPosition="fixed"
            />
          </div>

          {readyToVerify && (
            <div className="rounded-xl border bg-muted/50 px-4 py-3">
              {verifying ? (
                <span className="flex items-center gap-2 text-[13px] font-medium text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" />
                  Checking account…
                </span>
              ) : resolvedName ? (
                <span className="text-[14px] font-semibold text-foreground">
                  {resolvedName}
                </span>
              ) : (
                <span className="text-[13px] font-medium text-destructive">
                  Could not verify this account. Check the details and try
                  again.
                </span>
              )}
            </div>
          )}

          {resolvedName && (
            <label className="flex items-center gap-3">
              <Checkbox
                checked={isChecked}
                onCheckedChange={(checked) => setIsChecked(checked === true)}
              />
              <span className="text-[13px] font-semibold text-foreground">
                Make default bank
              </span>
            </label>
          )}

          <DialogFooter>
            <Button
              type="submit"
              disabled={!canSubmit || completeAddBank.isPending}
              className="h-[52px] w-full rounded-xl text-[15px] font-semibold"
            >
              {completeAddBank.isPending ? (
                <Loader2 className="size-5 animate-spin" />
              ) : (
                "Confirm"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddBanks;
